import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import "./MobileSearch.css"
import Icon from "../../base/Icon"
import { openJobQuickView, useHub } from "../../../core/Global"
import { API_ROUTES } from "../../../core/Constants"
import SearchItem from "../../../models/SearchItem"


export interface MobileSearchRef {
    open: () => void
}

const MobileSearch = forwardRef<MobileSearchRef>(function MobileSearch(_props, ref) {

    const hub = useHub()
    const inputRef = useRef<HTMLInputElement>(null)

    const [open, openSet] = useState<boolean>(false)
    const [keyword, keywordSet] = useState<string>("")
    const [searching, searchingSet] = useState<boolean>(false)
    const [data, dataSet] = useState<SearchItem[]>([])

    // Track the visible viewport so the overlay's bottom edge (where the search
    // box lives) always sits right on top of the on-screen keyboard.
    const [vp, vpSet] = useState<{ h: number, top: number }>({
        h: window.innerHeight,
        top: 0
    })

    // Exposed so the tab-bar search button opens + focuses synchronously inside
    // its tap gesture — the only reliable way to open the iOS keyboard.
    useImperativeHandle(ref, () => ({
        open: () => {
            inputRef.current?.focus()
            openSet(true)
        }
    }), [])

    useEffect(() => {
        const vv = window.visualViewport
        if (!vv) return
        const onResize = () => vpSet({ h: vv.height, top: vv.offsetTop })
        onResize()
        vv.addEventListener("resize", onResize)
        vv.addEventListener("scroll", onResize)
        return () => {
            vv.removeEventListener("resize", onResize)
            vv.removeEventListener("scroll", onResize)
        }
    }, [])

    // On open: re-assert focus (fallback for platforms where the gesture focus
    // didn't stick). On close: dismiss the keyboard and reset the search.
    useEffect(() => {
        if (open) {
            inputRef.current?.focus()
        } else {
            inputRef.current?.blur()
            keywordSet("")
            dataSet([])
            searchingSet(false)
        }
    }, [open])

    useEffect(() => {
        if (keyword.trim().length > 2) {
            searchingSet(true)
            const t = setTimeout(() => {
                hub.Post(API_ROUTES.Search, keyword, res => {
                    dataSet(res.data ?? [])
                    searchingSet(false)
                })
            }, 500)
            return () => clearTimeout(t)
        } else {
            dataSet([])
            searchingSet(false)
        }
    }, [keyword])

    const close = () => {
        inputRef.current?.blur()
        openSet(false)
    }

    const openJob = (id?: string) => {
        // Dismiss the keyboard but keep the search overlay open underneath, so
        // closing the job drawer returns the user to their search results.
        inputRef.current?.blur()
        openJobQuickView(id)
    }

    const jobs = data.filter(s => s.job)

    return (
        <div className={`mobile-search-overlay ${open ? "open" : ""}`}
            style={{ height: vp.h, top: vp.top }}>

            <div className="mobile-search-results">
                {
                    searching ?
                        <div className="mobile-search-loading">
                            <span className="spinner-border text-primary" />
                            <div className="mt-3">Searching...</div>
                        </div>
                        :
                    jobs.length > 0 ?
                        jobs.map((s, k) =>
                            <div key={k} className="mobile-search-result"
                                onClick={() => openJob(s.job?.id)}>

                                <div className="d-flex align-items-center mb-2">
                                    <Icon icon="circle" type="solid" size={10}
                                        color={s.job?.jobStatus?.color} className="me-2" />
                                    <span className="fw-bold me-2">#{s.job?.jobId}</span>
                                    <span className="text-muted fs-8">{s.job?.jobStatus?.name}</span>
                                </div>

                                <div className="mobile-search-result-line">
                                    <Icon icon="user" type="solid" size={12} className="me-2 text-gray-500" />
                                    <span>{s.job?.fullName}</span>
                                    {s.job?.phone?.phoneFriendly &&
                                        <>
                                            <Icon icon="phone" type="solid" size={12} className="ms-4 me-2 text-gray-500" />
                                            <span>{s.job?.phone?.phoneFriendly}</span>
                                        </>
                                    }
                                </div>

                                {s.job?.address?.address?.label &&
                                    <div className="mobile-search-result-line">
                                        <Icon icon="location-dot" type="solid" size={12} className="me-2 text-gray-500" />
                                        <span>{s.job?.address?.address?.label}</span>
                                    </div>
                                }

                                <div className="mobile-search-result-line">
                                    <Icon icon="wrench" type="solid" size={12} className="me-2 text-gray-500" />
                                    <span className="me-4">{s.job?.jobType ?? "-"}</span>
                                    <Icon icon="bullhorn" type="solid" size={12} className="me-2 text-gray-500" />
                                    <span>{s.job?.jobSource ?? "-"}</span>
                                </div>
                            </div>
                        )
                        :
                        <div className="mobile-search-empty">
                            {
                                keyword.trim().length > 2 && !searching ?
                                    "No jobs found."
                                    :
                                    "Search jobs by name, phone, address, job # or any keyword."
                            }
                        </div>
                }
            </div>

            <div className="mobile-search-footer">
                <div className="mobile-search-box">
                    <Icon icon="magnifying-glass" size={18} className="mobile-search-box-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="mobile-search-input"
                        placeholder="Search jobs..."
                        enterKeyHint="search"
                        value={keyword}
                        onChange={e => keywordSet(e.target.value)}
                    />
                    {searching && <span className="spinner-border spinner-border-sm text-gray-400" />}
                    {!searching && keyword.length > 0 &&
                        <Icon icon="circle-xmark" type="solid" size={18}
                            className="mobile-search-clear"
                            onClick={() => { keywordSet(""); inputRef.current?.focus() }} />
                    }
                </div>

                <div className="mobile-search-close" onClick={close}>
                    <Icon icon="xmark" size={20} />
                </div>
            </div>
        </div>
    )
})

export default MobileSearch
